import { FocusControl } from "silver";

export function toggleChildNodeAvailability(root: HTMLElement, available: boolean, filter?: (child: HTMLElement) => boolean): void {
	const children = root.querySelectorAll(FocusControl.FOCUSABLE_QUERY) as NodeListOf<HTMLElement>;
	for (let i = 0; i < children.length; i++) {
		const child = children[i];
		if (filter && !filter(child)) {
			continue;
		}
		if (!available && child.tabIndex !== -1) {
			child.setAttribute("data-restore-tabindex", String(child.tabIndex));
			child.tabIndex = -1;
		} else if (available && child.hasAttribute("data-restore-tabindex")) {
			child.tabIndex = Number(child.getAttribute("data-restore-tabindex"));
		}
		child.setAttribute("aria-hidden", available ? "false" : "true");
	}
}

export function generateId(prefix: string = ""): string {
	const id = Math.random().toString(36).replace(/[^a-z]+/g, "").substr(0, 5);
	return prefix + id;
}

export function htmlToNode(html: string): Node {
	const template = document.createElement("template");
	template.innerHTML = html.trim();
	if (template.content.childNodes.length == 1 && template.content.firstChild) {
		return template.content.firstChild;
	}
	return template.content;
}

export function escapeHTML(text: string): string {
	const node = document.createTextNode(text);
	return node.textContent || "";
}

export function textToParagraphs(text: string): HTMLParagraphElement[] {
	return text.split(/\n/g).map(par => {
		const p = document.createElement("p");
		p.innerText = par;
		return p;
	});
}

export function cacheBustURL(url: string): string {
	/** This will need to be updated if we ever don't use require */
	if (!window.hasOwnProperty("require")) {
		console.warn("Require not running");
		return url;
	}
	return ((window as any).require as { toUrl: (url: string) => string }).toUrl(url);
}

export function makeTemplateGenerator(sourceHTML: string): () => Node {
	let template: HTMLTemplateElement|undefined;
	return () => {
		if (!template) {
			template = document.createElement("template");
			template.innerHTML = sourceHTML;
		}
		return template.content.cloneNode(true);
	};
}

export function wait(milliseconds: number): Promise<void> {
	return new Promise(resolve => {
		setTimeout(() => resolve(), milliseconds);
	});
}

export async function multiAttempt<T>(attempts: number, callback: () => Promise<T>): Promise<T> {
	for (let i = 0; i < attempts; i++) {
		try {
			return await callback();
		} catch (er) {
			if (i === attempts - 1) {
				throw er;
			}
			console.error(er);
		}
	}
	throw new Error("failed"); // should have thrown error before here. This is just for ts
}


export function fromEntries<T>(iterable: Iterable<[string|number, T]>): { [key: string]: T } {
	const out: { [key: string]: any } = {};
	for (const pair of iterable) {
		if (Object(pair) !== pair) {
			throw new TypeError("iterable for fromEntries should yield objects");
		}

		const { "0": key, "1": val } = pair;

		Object.defineProperty(out, key, {
			configurable: true,
			enumerable:   true,
			writable:     true,
			value:        val
		});
	}
	return out;
}

export function keyBy<T>(key: string & keyof T, iterable: Iterable<T>): { [ keyValue: string ]: T } {
	const out: { [ keyValue: string ]: T } = { };
	for (const entry of iterable) {
		if (Object(entry) !== entry) {
			throw new TypeError("iterable for fromEntries should yield objects");
		}
		const keyValue = entry[key];
		out[String(keyValue)] = entry;
	}
	return out;
}

// Events passed through the "click" event handlers are "MouseEvent"s, even if they were initiated by a keyboard "Enter"
export function isKeyboardClick(e: MouseEvent): boolean {
	return (e as any).detail === 0;
}

export function clamp(value: number, min: number, max: number): number {
	return Math.max(min, Math.min(value, max));
}

/**
 * Guards a function from being called multiple times in a row.
 * Delay is configurable, but removes the function from the
 * call-stack, so guaranteed to be after execution finishes.
 * Because it's out of the call stack, function must return void.
 */
export function debounce<T extends (this: any, ...args: any[]) => void>(callback: T, delay: number = 1): T {
	let handle: ReturnType<typeof setTimeout>|undefined;
	return function(this: any, ...args: any[]): void {
		if (handle) {
			clearTimeout(handle);
		}
		handle = setTimeout(callback.bind(this, ...args), delay);
	} as T;
}

export function isNotNull<T>(value: T|null): value is T {
	return value !== null;
}

export function range(start: number, toInclusive: number, stepSize: number = 1): number[] {
	const out: number[] = [];
	for (let i = start; i <= toInclusive; i += stepSize) {
		out.push(i);
	}
	return out;
}

export function memoize<I extends Array<string|number|boolean|null>, O>(func: (...args: I) => O): ((...args: I) => O) {
	const cache: { [ key: string ]: O } = { };
	return (...args: I): O => {
		const key = JSON.stringify(args);
		if (cache.hasOwnProperty(key)) {
			return cache[key];
		}
		return cache[key] = func(...args);
	};
}

/** Like memoize, but arguments are ignored if a value has ever been set. Caches at most 1 response */
export function cacheFirstResults<I extends any[], O>(factory: (...args: I) => O): ((...args: I) => O) {
	let value: O|undefined;
	return (...args: I): O => {
		if (value === undefined) {
			value = factory(...args);
		}
		return value;
	};
}


const SanitizeRegex = /[\s!-#%-\x2A,-/:;\x3F@\x5B-\x5D_\x7B}\u00A1\u00A7\u00AB\u00B6\u00B7\u00BB\u00BF\u037E\u0387\u055A-\u055F\u0589\u058A\u05BE\u05C0\u05C3\u05C6\u05F3\u05F4\u0609\u060A\u060C\u060D\u061B\u061E\u061F\u066A-\u066D\u06D4\u0700-\u070D\u07F7-\u07F9\u0830-\u083E\u085E\u0964\u0965\u0970\u0AF0\u0DF4\u0E4F\u0E5A\u0E5B\u0F04-\u0F12\u0F14\u0F3A-\u0F3D\u0F85\u0FD0-\u0FD4\u0FD9\u0FDA\u104A-\u104F\u10FB\u1360-\u1368\u1400\u166D\u166E\u169B\u169C\u16EB-\u16ED\u1735\u1736\u17D4-\u17D6\u17D8-\u17DA\u1800-\u180A\u1944\u1945\u1A1E\u1A1F\u1AA0-\u1AA6\u1AA8-\u1AAD\u1B5A-\u1B60\u1BFC-\u1BFF\u1C3B-\u1C3F\u1C7E\u1C7F\u1CC0-\u1CC7\u1CD3\u2010-\u2027\u2030-\u2043\u2045-\u2051\u2053-\u205E\u207D\u207E\u208D\u208E\u2329\u232A\u2768-\u2775\u27C5\u27C6\u27E6-\u27EF\u2983-\u2998\u29D8-\u29DB\u29FC\u29FD\u2CF9-\u2CFC\u2CFE\u2CFF\u2D70\u2E00-\u2E2E\u2E30-\u2E3B\u3001-\u3003\u3008-\u3011\u3014-\u301F\u3030\u303D\u30A0\u30FB\uA4FE\uA4FF\uA60D-\uA60F\uA673\uA67E\uA6F2-\uA6F7\uA874-\uA877\uA8CE\uA8CF\uA8F8-\uA8FA\uA92E\uA92F\uA95F\uA9C1-\uA9CD\uA9DE\uA9DF\uAA5C-\uAA5F\uAADE\uAADF\uAAF0\uAAF1\uABEB\uFD3E\uFD3F\uFE10-\uFE19\uFE30-\uFE52\uFE54-\uFE61\uFE63\uFE68\uFE6A\uFE6B\uFF01-\uFF03\uFF05-\uFF0A\uFF0C-\uFF0F\uFF1A\uFF1B\uFF1F\uFF20\uFF3B-\uFF3D\uFF3F\uFF5B\uFF5D\uFF5F-\uFF65]+/g;


export function sanitize(input: string, replaceWith: string = " "): string {
	return input.trim()
		.replace(SanitizeRegex, replaceWith)
		.replace(new RegExp(`^${replaceWith}+|${replaceWith}+$`, "g"), "");
}

export function countWords(text: string): number {
	text = sanitize(text);
	if (!text) {
		return 0;
	}
	return text.split(" ").length;
}

export function getTreeNode<T>(root: any, keys: Array<string|number>, defaultValue: T): T {
	let node = root;
	for (let i = 0; i < keys.length; i++) {
		if (!node.hasOwnProperty(keys[i])) {
			node[keys[i]] = (i === keys.length - 1) ? defaultValue : { };
		}
		node = node[keys[i]];
	}
	return node;
}

export function getDateFromEpochTimestamp(epochTimestamp: number): Date {
	return new Date(epochTimestamp * 1000);
}

/**
 * Converts a value on a linear scale to its corresponding position in a exponential scale
 */
export function perceptualUniformity(input: number, inputMin: number, inputMax: number, outputMin: number, outputMax: number): number {
	const steps = (inputMax - inputMin);
	const ratio = (outputMax / outputMin);
	const base  = Math.pow(ratio, 1 / steps);

	return Math.min(outputMax, Math.max(outputMin, outputMin * Math.pow(base, input - inputMin)));
}

export function formatSexagesimal(value: number|null, places: number = 2, decimals: number = 0): string {
	let out: string = "";
	if (value === null) {
		return "-" + Array(places).join(":--");
	}
	const div = Math.pow(10, decimals);
	value = Math.floor(value * div) / div;

	for (let i = 0; i < places; i++) {
		if (out) {
			out = ":" + out;
		}
		out = String(value % 60).padStart(2, "0") + out;
		value = Math.floor(value / 60);
	}

	return out.replace(/^0/, ""); // trim a single leading 0
}
